<script setup lang="ts">
import { ref, computed, onUnmounted } from 'vue'
import { t, qualityLabels } from '@/i18n'
import { useExport } from '@/composables/useExport'
import { validateFile, decodeErrorKey } from '@/composables/useAudio'
import { trackEvent } from '@/utils/analytics'
import type { ExportFormat } from '@/types'
import FileUpload from '@/components/FileUpload.vue'

// Ad-hoc audio converter: decode any supported file and re-encode the ENTIRE
// file as MP3 or WAV. No waveform, no trimming - load, pick format, download.
// Reuses the same decode path (decodeAudioData) and encoder worker (lamejs /
// WAV writer) as the trimmer, just without the selection step.

const { isExporting, exportProgress, exportAudio, cancelExport, cleanup } = useExport()

// State
const loadedFile = ref<File | null>(null)
const audioBuffer = ref<AudioBuffer | null>(null)
const isLoading = ref(false)
const errorMessage = ref('')
const successMessage = ref('')

// Format + quality selection
const format = ref<ExportFormat>('mp3')
const bitrateOptions = [
  { value: 128, label: qualityLabels.value.low },
  { value: 192, label: qualityLabels.value.medium },
  { value: 256, label: qualityLabels.value.high },
  { value: 320, label: qualityLabels.value.best }
]
const bitrate = ref(192)

// AudioContext (lazy, reused across loads)
let audioContext: AudioContext | null = null
const getAudioContext = (): AudioContext => {
  if (!audioContext) {
    audioContext = new (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)()
  }
  return audioContext
}

// File info
const fileDuration = computed(() => audioBuffer.value?.duration || 0)
const formattedDuration = computed(() => {
  const total = Math.round(fileDuration.value)
  const minutes = Math.floor(total / 60)
  const seconds = total % 60
  return `${minutes}:${String(seconds).padStart(2, '0')}`
})
const formattedSize = computed(() => {
  if (!loadedFile.value) return ''
  const mb = loadedFile.value.size / (1024 * 1024)
  return mb >= 1 ? `${mb.toFixed(1)} MB` : `${Math.round(loadedFile.value.size / 1024)} KB`
})

const messageFor = (key?: string): string => {
  const msg = (t.value as unknown as Record<string, unknown>)[key || 'invalidFile']
  return typeof msg === 'string' ? msg : t.value.invalidFile
}

// Decode the selected file (whole file, no waveform generation)
const handleFileSelect = async (file: File) => {
  errorMessage.value = ''
  successMessage.value = ''

  const validation = validateFile(file)
  if (!validation.valid) {
    errorMessage.value = messageFor(validation.error)
    return
  }

  trackEvent('file_upload', {
    file_type: file.type,
    file_size: file.size,
    file_extension: file.name.split('.').pop()
  })

  isLoading.value = true
  try {
    const arrayBuffer = await file.arrayBuffer()
    const buffer = await getAudioContext().decodeAudioData(arrayBuffer)
    loadedFile.value = file
    audioBuffer.value = buffer
  } catch (err) {
    loadedFile.value = null
    audioBuffer.value = null
    errorMessage.value = messageFor(decodeErrorKey(err))
    trackEvent('file_upload_error', { error: 'decode_failed' })
  } finally {
    isLoading.value = false
  }
}

// Convert the entire file to the selected format
const handleConvert = async () => {
  if (!audioBuffer.value || !loadedFile.value || isExporting.value) return

  trackEvent('export_start', {
    format: format.value,
    bitrate: format.value === 'mp3' ? bitrate.value : undefined,
    duration: fileDuration.value
  })

  const buffer = audioBuffer.value
  // Pass the AudioBuffer's channel data directly - useExport copies the
  // underlying buffers before transferring them to the worker
  const channels: Float32Array[] = []
  for (let ch = 0; ch < buffer.numberOfChannels; ch++) {
    channels.push(buffer.getChannelData(ch))
  }

  try {
    await exportAudio(channels, buffer.sampleRate, format.value, loadedFile.value.name, bitrate.value)
    successMessage.value = t.value.exportSuccess
    trackEvent('export_complete', { format: format.value })
  } catch (err) {
    if (import.meta.env.DEV) console.error('Convert error:', err)
    if (err instanceof Error && err.message !== 'Encoding cancelled') {
      errorMessage.value = t.value.exportError
      trackEvent('export_error', { format: format.value, error: err.message })
    } else {
      trackEvent('export_cancel', { format: format.value })
    }
  }
}

// Reset for a new file
const handleReset = () => {
  loadedFile.value = null
  audioBuffer.value = null
  errorMessage.value = ''
  successMessage.value = ''
}

const roundedProgress = computed(() => Math.round(exportProgress.value))

onUnmounted(() => {
  cleanup()
})
</script>

<template>
  <div class="converter">
    <!-- File selection (drag & drop / picker) -->
    <FileUpload
      v-if="!loadedFile"
      :is-loading="isLoading"
      @file-select="handleFileSelect"
    />

    <!-- Converter panel -->
    <div v-else class="converter-panel card">
      <!-- Loaded file info -->
      <div class="file-info">
        <div class="file-details">
          <span class="file-name" :title="loadedFile.name">{{ loadedFile.name }}</span>
          <span class="file-meta">{{ formattedDuration }} · {{ formattedSize }}</span>
        </div>
        <button class="btn btn-secondary" :disabled="isExporting" @click="handleReset">
          {{ t.reset }}
        </button>
      </div>

      <!-- Output format -->
      <div class="field">
        <span class="field-label">{{ t.format }}</span>
        <div class="format-options" role="radiogroup" :aria-label="t.format">
          <button
            class="format-option"
            :class="{ active: format === 'mp3' }"
            role="radio"
            :aria-checked="format === 'mp3'"
            :disabled="isExporting"
            @click="format = 'mp3'"
          >
            MP3
          </button>
          <button
            class="format-option"
            :class="{ active: format === 'wav' }"
            role="radio"
            :aria-checked="format === 'wav'"
            :disabled="isExporting"
            @click="format = 'wav'"
          >
            WAV
          </button>
        </div>
      </div>

      <!-- Quality (MP3 only) -->
      <div v-if="format === 'mp3'" class="field">
        <label class="field-label" for="converter-quality">{{ t.quality }}</label>
        <select
          id="converter-quality"
          v-model.number="bitrate"
          class="quality-select"
          :disabled="isExporting"
        >
          <option v-for="option in bitrateOptions" :key="option.value" :value="option.value">
            {{ option.label }}
          </option>
        </select>
      </div>

      <!-- Convert button / progress -->
      <button v-if="!isExporting" class="btn btn-primary btn-lg convert-btn" @click="handleConvert">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
          <polyline points="7 10 12 15 17 10" />
          <line x1="12" x2="12" y1="15" y2="3" />
        </svg>
        {{ t.export }}
      </button>

      <div v-else class="convert-progress" role="progressbar" :aria-valuenow="roundedProgress" aria-valuemin="0" aria-valuemax="100" :aria-label="t.processing">
        <div class="progress-track">
          <div class="progress-fill" :style="{ width: `${roundedProgress}%` }"></div>
        </div>
        <div class="progress-info">
          <span>{{ t.processing }}</span>
          <span>{{ roundedProgress }}%</span>
        </div>
        <button class="btn btn-secondary btn-sm" @click="cancelExport">{{ t.cancel }}</button>
      </div>

      <!-- Status messages -->
      <p v-if="errorMessage" class="status-message error" role="alert">{{ errorMessage }}</p>
      <p v-else-if="successMessage" class="status-message success" role="status">
        {{ successMessage }} {{ t.download }}
      </p>
    </div>

    <!-- Load failures happen before the panel exists, so show them under the picker -->
    <p v-if="!loadedFile && errorMessage" class="status-message error picker-error" role="alert">{{ errorMessage }}</p>
  </div>
</template>

<style scoped>
.converter {
  min-height: 339px; /* Match FileUpload to prevent CLS */
}

.converter-panel {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding: 2rem;
  box-shadow: var(--shadow-md);
}

.converter-panel:hover {
  box-shadow: var(--shadow-md);
}

.file-info {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding-bottom: 1.25rem;
  border-bottom: 1px solid var(--border-color);
}

.file-details {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  min-width: 0;
}

.file-name {
  font-weight: 600;
  color: var(--text-primary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.file-meta {
  color: var(--text-secondary);
  font-size: 0.875rem;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.field-label {
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--text-primary);
}

.format-options {
  display: flex;
  gap: 0.75rem;
}

.format-option {
  flex: 1;
  padding: 0.75rem 1rem;
  font-size: 1rem;
  font-weight: 600;
  border: 2px solid var(--border-color);
  border-radius: var(--radius-md);
  background: var(--card-background);
  color: var(--text-secondary);
  cursor: pointer;
  transition: border-color var(--transition-fast), color var(--transition-fast), background-color var(--transition-fast);
}

.format-option:hover:not(:disabled) {
  border-color: var(--primary-color);
}

.format-option.active {
  border-color: var(--primary-color);
  color: var(--primary-color);
  background: var(--hover-tint);
}

.format-option:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.quality-select {
  padding: 0.75rem 1rem;
  font-size: 1rem;
  border: 2px solid var(--border-color);
  border-radius: var(--radius-md);
  background: var(--card-background);
  color: var(--text-primary);
  cursor: pointer;
}

.quality-select:focus {
  outline: 2px solid var(--primary-color);
  outline-offset: 2px;
}

.convert-btn {
  width: 100%;
}

.convert-progress {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.progress-track {
  height: 8px;
  border-radius: 4px;
  background: var(--waveform-background);
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  border-radius: 4px;
  background: var(--primary-color);
  transition: width 0.2s ease;
}

.progress-info {
  display: flex;
  justify-content: space-between;
  font-size: 0.875rem;
  color: var(--text-secondary);
}

.status-message {
  margin: 0;
  font-size: 0.9375rem;
  text-align: center;
}

.status-message.error {
  color: var(--status-error-fg);
  background: var(--status-error-bg);
  padding: 0.75rem 1rem;
  border-radius: var(--radius-md);
}

.status-message.success {
  color: var(--status-success-fg);
  background: var(--status-success-bg);
  padding: 0.75rem 1rem;
  border-radius: var(--radius-md);
}

@media (max-width: 640px) {
  .converter {
    min-height: 250px;
  }

  .converter-panel {
    padding: 1.25rem 0.75rem;
    gap: 1.25rem;
  }

  .file-info {
    flex-direction: column;
    align-items: flex-start;
  }
}
.picker-error {
  margin-top: 1rem;
}
</style>
